import _ from 'lodash';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { INTERNAL_ORG_IDS_FOR_IMPERSONATION } from 'states/profile/business-rules';
import { sortRecordsByName } from 'states/resources/organizations/business-logic';
/** import multi application imports in the section above, Sort Lines Ascending */

const INTERNAL_ORG_IDS = new Set(INTERNAL_ORG_IDS_FOR_IMPERSONATION);

const GROUPS_WITH_PARENT_SUFFIX = new Set(['advertiser', 'partner', 'sales_rep', 'other']);

const IMPERSONATE_ORG_GROUPS = [
    { key: 'internal', label: 'Internal', match: org => INTERNAL_ORG_IDS.has(org.id) },
    {
        key: 'media_buyer',
        label: 'Self Serve Media Buyers',
        match: org => org.type === 'media_buyer',
    },
    {
        key: 'advertiser',
        label: 'Advertisers',
        match: org => org.client_type === 'advertiser',
    },
    { key: 'partner', label: 'Partners', match: org => org.client_type === 'partner' },
    { key: 'sales_rep', label: 'Sales Reps', match: org => org.client_type === 'sales_rep' },
    { key: 'other', label: 'Other Organizations', match: () => true },
];

/**
 * Groups impersonation orgs for the sidebar: Internal ids first, then remaining
 * orgs in exclusive sidebar order (media_buyer by Organization.type; advertiser,
 * partner, and sales_rep by client_type). Leftover orgs (admin not in Internal,
 * co_managed_client/client with other or empty client_type, etc.) go last as
 * Other Organizations. Empty groups are omitted. Advertiser/partner/sales_rep
 * are never grouped by org.type.
 *
 * Each org is copied with `displayParentName`: true for advertiser, partner,
 * sales_rep, and other when `parent` is a different org with a name. Internal
 * and media_buyer stay false. The sidebar suffix and parent search read
 * `parent` when this flag is true.
 *
 * @param {Array<Object>} organizations
 * @param {Object} [organizations[].parent]
 * @param {string} [organizations[].parent.id]
 * @param {string} [organizations[].parent.name]
 * @returns {Array<{key: string, label: string, orgs: Array<Object>}>}
 */
function groupOrganizationsByType(organizations) {
    const seenIds = new Set();
    const buckets = (organizations || []).reduce(
        (acc, org) => {
            if (!org.users?.length) {
                return acc;
            }

            if (seenIds.has(org.id)) {
                return acc;
            }

            seenIds.add(org.id);

            const group = IMPERSONATE_ORG_GROUPS.find(({ match }) => match(org));
            const displayParentName =
                GROUPS_WITH_PARENT_SUFFIX.has(group.key) &&
                org.parent?.id &&
                org.parent?.name &&
                org.parent.id !== org.id;
            acc[group.key].push({ ...org, displayParentName });

            return acc;
        },
        Object.fromEntries(IMPERSONATE_ORG_GROUPS.map(({ key }) => [key, []]))
    );

    return IMPERSONATE_ORG_GROUPS.map(({ key, label }) => ({
        key,
        label,
        orgs: sortRecordsByName(buckets[key]),
    })).filter(group => group.orgs.length > 0);
}

export default function(state) {
    const currentUserId = _.get(state, 'profile.userId', undefined);
    const realPersonId = _.get(state, 'profile.impersonatorsToken.user.id', undefined);
    const realPersonOrganization = _.get(
        state,
        'profile.impersonatorsToken.v3_impersonator.organization',
        undefined
    );
    const isImpersonating = _.get(state, 'profile.isImpersonating', false);

    const organizations = _.get(state, 'impersonate.organizations', undefined);
    const isProfileLoaded = _.get(state, `impersonate.isProfileLoaded`, false);
    const isInitialized = _.get(state, `impersonate.isInitialized`, false);
    const isLoading = _.get(state, `impersonate.isLoading`, false);
    const isModalOpen = _.get(state, `impersonate.isModalOpen`, false);
    const selectedOrganization = _.get(state, `impersonate.selectedOrganization`);
    const errors = _.get(state, `impersonate.errors`, []);

    const userOrganizationId = _.get(state, 'profile.organizationId');
    const userFirstName = _.get(state, 'profile.userFirstName');

    const userOrganizationName = _.get(state, 'profile.ownOrganization.name');

    const orgGroups = groupOrganizationsByType(organizations);
    const sanitizedOrgToUserMappings = orgGroups.flatMap(group => group.orgs);

    return {
        isProfileLoaded,
        isInitialized,
        isLoading,
        isModalOpen,
        errors,

        userFirstName,
        userOrganizationId,
        userOrganizationName,
        currentUserId,
        realPersonOrganization,
        realPersonId,

        organizations: sanitizedOrgToUserMappings,
        orgGroups,
        isImpersonating,
        selectedOrganization,
    };
}
